import { useEffect, useState } from "react";
import { SpeakerHigh, X } from "@phosphor-icons/react";
import { useAudioReadiness } from "@/hooks/useAudioReadiness";
import { previewNotifBeep } from "@/hooks/useNotificationSoundPref";

const DISMISS_KEY = "hub.notif.sound_banner_dismissed_at";

/**
 * "Habilitar sonido" fallback banner.
 *
 * Shown ONLY when:
 *   1. The user has enabled "Sonido de notificaciones" in the TopBar toggle
 *      (soundEnabled prop === true).
 *   2. The shared AudioContext is still blocked by Chrome's autoplay policy
 *      (no user gesture has resumed it yet).
 *   3. The user has not previously dismissed the banner in this browser
 *      (`hub.notif.sound_banner_dismissed_at` in localStorage).
 *
 * "Habilitar" resumes the AudioContext (the click itself is the required
 * user gesture) and plays the preview chime so the user gets audible
 * confirmation. Auto-hides on the resulting state change.
 */
export default function AudioReadinessBanner({ soundEnabled }) {
    const { blocked, unlock } = useAudioReadiness();
    const [dismissed, setDismissed] = useState(() => {
        try { return !!localStorage.getItem(DISMISS_KEY); }
        catch { return false; }
    });

    // Clear the dismiss flag if the user turns the toggle OFF, so if they
    // ever turn it back ON with a still-suspended context we surface the
    // banner again. (When the toggle click itself is the user gesture the
    // banner shouldn't reappear at all — this is a defence-in-depth path.)
    useEffect(() => {
        if (!soundEnabled) {
            try { localStorage.removeItem(DISMISS_KEY); } catch { /* ignore */ }
            setDismissed(false);
        }
    }, [soundEnabled]);

    if (!soundEnabled || !blocked || dismissed) return null;

    const onEnable = async () => {
        await unlock();
        // A click is a user gesture — play the preview so the agent hears
        // confirmation the audio path is unblocked.
        previewNotifBeep();
    };

    const onDismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); }
        catch { /* quota / SSR — dismissal only lives for the session */ }
        setDismissed(true);
    };

    return (
        <div
            // vh-safe-top: respect env(safe-area-inset-top) on iOS PWA
            // standalone so the banner doesn't sit UNDER the status bar
            // (clock + wifi/battery). Renders as 0 on desktop, ~47px on
            // iPhones with notch. Same pattern as InboxMobile's TopBar.
            className="flex items-center gap-2 sm:gap-3 px-4 py-2 sm:py-2 pt-2 border-b border-amber-200 bg-amber-50 text-amber-900 shrink-0 vh-safe-top"
            role="status"
            data-testid="audio-readiness-banner"
        >
            <SpeakerHigh size={16} weight="fill" className="text-amber-700 shrink-0" />
            <span className="text-xs sm:text-sm flex-1 min-w-0">
                El sonido de notificaciones requiere que hagas click en la página primero.
            </span>
            <button
                type="button"
                onClick={onEnable}
                className="vh-tappable shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full bg-amber-600 text-white hover:bg-amber-700 transition-colors"
                data-testid="audio-readiness-banner-enable"
            >
                Habilitar
            </button>
            <button
                type="button"
                onClick={onDismiss}
                className="vh-tappable shrink-0 w-6 h-6 rounded-md text-amber-700 hover:bg-amber-100 flex items-center justify-center transition-colors"
                aria-label="Descartar aviso de sonido"
                title="Descartar"
                data-testid="audio-readiness-banner-dismiss"
            >
                <X size={12} weight="bold" />
            </button>
        </div>
    );
}
